/**
 * Date utility functions for handling local timezone dates
 * All functions work with the device's local timezone
 */

/**
 * Converts a Date object or date string to YYYY-MM-DD format in local timezone
 * @param date - Date object or date string to convert
 * @returns Date string in YYYY-MM-DD format (local timezone)
 */
export const toLocalDateString = (date: Date | string): string => {
  const d = date instanceof Date ? date : parseLocalDate(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parses a date string or Date object to local Date object at midnight
 * @param dateInput - Date string (YYYY-MM-DD or ISO) or Date object
 * @returns Date object set to midnight in local timezone
 */
export const parseLocalDate = (dateInput: string | Date): Date => {
  if (dateInput instanceof Date) {
    const date = new Date(dateInput);
    date.setHours(0, 0, 0, 0);
    return date;
  }
  
  // Handle YYYY-MM-DD format (local date)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [year, month, day] = dateInput.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // Handle ISO string - convert to local date
  const date = new Date(dateInput);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Compares two dates (ignoring time) in local timezone
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if dates are the same day in local timezone
 */
export const isSameLocalDate = (date1: Date | string, date2: Date | string): boolean => {
  const d1 = parseLocalDate(date1);
  const d2 = parseLocalDate(date2);
  return toLocalDateString(d1) === toLocalDateString(d2);
};

/**
 * Checks if a date is between start and end dates (inclusive) in local timezone
 * @param date - Date to check
 * @param startDate - Start date
 * @param endDate - End date
 * @returns true if date is within range
 */
export const isDateInRange = (
  date: Date | string,
  startDate: Date | string,
  endDate: Date | string
): boolean => {
  const checkDate = parseLocalDate(date);
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  return checkDate >= start && checkDate <= end;
};

/**
 * Formats a date for display in local timezone
 * @param date - Date to format
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export const formatLocalDate = (
  date: Date | string,
  options: Intl.DateTimeFormatOptions = { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }
): string => {
  const d = parseLocalDate(date);
  return d.toLocaleDateString('en-US', options);
};

/**
 * Formats a date and time for display in local timezone
 * @param date - Date to format
 * @returns Formatted date and time string
 */
export const formatLocalDateTime = (date: Date | string): string => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Gets the current local date at midnight
 * @returns Date object set to today at midnight
 */
export const getTodayLocal = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * Checks if a date is today in local timezone
 * @param date - Date to check
 * @returns true if date is today
 */
export const isToday = (date: Date | string): boolean => {
  return isSameLocalDate(date, getTodayLocal());
};

/**
 * Checks if a date is in the past (before today) in local timezone
 * @param date - Date to check
 * @returns true if date is before today
 */
export const isPastDate = (date: Date | string): boolean => {
  const checkDate = parseLocalDate(date);
  const today = getTodayLocal();
  return checkDate < today;
};

/**
 * Checks if a date is in the future (after today) in local timezone
 * @param date - Date to check
 * @returns true if date is after today
 */
export const isFutureDate = (date: Date | string): boolean => {
  const checkDate = parseLocalDate(date);
  const today = getTodayLocal();
  return checkDate > today;
};

/**
 * Adds days to a date in local timezone
 * @param date - Starting date
 * @param days - Number of days to add (can be negative)
 * @returns New date with days added
 */
export const addDays = (date: Date | string, days: number): Date => {
  const d = parseLocalDate(date);
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Gets the day name for a date in local timezone
 * @param date - Date to get day name for
 * @returns Day name (e.g., 'monday', 'tuesday')
 */
export const getDayName = (date: Date | string): string => {
  const d = parseLocalDate(date);
  return d.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
};
